// File attachments panel for TaskDetail. Lists existing attachments + upload
// button + drag-drop zone (multi/batch) + preview / download / delete actions.
//
// Permissions:
//   - List: any authenticated user
//   - Upload: any authenticated user
//   - Download / preview: any authenticated user
//   - Delete: only the uploader, OR an admin
//
// Limits (must match worker/src/index.ts):
//   - Max 25MB per file
//   - Allowed mime: image/*, application/pdf, msword/excel/openxml, text/plain, text/csv

import { useState, useEffect, useRef } from 'react';
import * as api from '../lib/api.ts';
import { Icon } from './Icon.jsx';
import { fmtDateTime } from '../lib/dates.ts';
import { isElevated } from '../lib/auth.ts';
import { confirmDialog } from '../lib/confirm.js';

const MAX_BYTES = 25 * 1024 * 1024;

function fmtSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

function fileIconKind(mime) {
  if (mime.startsWith('image/')) return '🖼️';
  if (mime === 'application/pdf') return '📄';
  if (mime.includes('word') || mime.includes('document')) return '📝';
  if (mime.includes('excel') || mime.includes('spreadsheet') || mime === 'text/csv') return '📊';
  return '📎';
}

function ext(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || '');
  return m ? m[1].toLowerCase() : '';
}

// What we can show inline, and how. Detected by extension — WPS Office emits
// non-standard MIMEs, but .docx/.xlsx payloads are OOXML regardless.
function previewKind(att) {
  const mime = att.mime || '';
  const e = ext(att.filename);
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf' || e === 'pdf') return 'pdf';
  if (e === 'docx') return 'word';
  if (e === 'xlsx') return 'excel';
  return null;  // .doc / .xls / legacy / other → download only
}

// Flatten an exceljs cell value to display text.
function cellText(cell) {
  const v = cell && cell.value;
  if (v == null) return '';
  if (typeof v !== 'object') return String(v);
  if (v instanceof Date) return v.toLocaleDateString();
  if (Array.isArray(v.richText)) return v.richText.map(t => t.text).join('');
  if (v.text != null) return String(v.text);       // hyperlink / rich text
  if (v.result != null) return String(v.result);   // formula result
  if (v.hyperlink) return String(v.hyperlink);
  return '';
}

// Renders a .docx into a scrollable container via docx-preview (lazy-loaded).
function WordView({ blob }) {
  const ref = useRef(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (!blob || !ref.current) return;
    const el = ref.current;
    el.innerHTML = '';
    import('docx-preview')
      .then(docx => { if (!cancelled) return docx.renderAsync(blob, el, undefined, { className: 'docx', inWrapper: true, ignoreLastRenderedPageBreak: true }); })
      .catch(e => { if (!cancelled) setErr(e.message || '无法渲染文档'); });
    return () => { cancelled = true; };
  }, [blob]);
  if (err) return <div className="att-modal-msg">{err}</div>;
  return <div className="att-modal-doc" ref={ref} />;
}

// Renders parsed .xlsx sheets as tables (row 0 = header).
function ExcelView({ sheets }) {
  if (!sheets || sheets.length === 0) return <div className="att-modal-msg">空表格</div>;
  return (
    <div className="att-modal-xlsx">
      {sheets.map((s, i) => (
        <div key={i} className="att-xlsx-sheet">
          {sheets.length > 1 && <div className="att-xlsx-name mono">{s.name}</div>}
          <table className="att-xlsx-table">
            <tbody>
              {s.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (r === 0
                    ? <th key={c}>{cell}</th>
                    : <td key={c}>{cell}</td>))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// In-app preview lightbox. Images + PDFs render via a blob URL; Word (.docx)
// renders client-side with docx-preview; Excel (.xlsx) is parsed with the
// already-bundled exceljs into a table. Everything stays client-side — the
// file is fetched authed and never leaves the worker (no 3rd-party viewer).
function PreviewModal({ att, kind, onClose, onDownload }) {
  const [state, setState] = useState('loading'); // loading | ready | error
  const [msg, setMsg] = useState('');
  const [url, setUrl] = useState('');             // image / pdf
  const [sheets, setSheets] = useState(null);     // excel
  const [wordBlob, setWordBlob] = useState(null); // word

  useEffect(() => {
    let cancelled = false;
    let madeUrl = '';
    setState('loading'); setMsg(''); setUrl(''); setSheets(null); setWordBlob(null);
    (async () => {
      try {
        const blob = await api.fetchAttachmentBlob(att.id);
        if (cancelled) return;
        if (kind === 'image' || kind === 'pdf') {
          madeUrl = URL.createObjectURL(blob);
          setUrl(madeUrl); setState('ready');
        } else if (kind === 'word') {
          setWordBlob(blob); setState('ready');
        } else if (kind === 'excel') {
          const buf = await blob.arrayBuffer();
          const ExcelJS = (await import('exceljs')).default;
          const wb = new ExcelJS.Workbook();
          await wb.xlsx.load(buf);
          if (cancelled) return;
          const out = [];
          wb.eachSheet(ws => {
            const rows = [];
            ws.eachRow({ includeEmpty: false }, row => {
              const cells = [];
              row.eachCell({ includeEmpty: true }, c => cells.push(cellText(c)));
              rows.push(cells);
            });
            out.push({ name: ws.name, rows: rows.slice(0, 300) }); // cap huge sheets
          });
          setSheets(out); setState('ready');
        } else {
          setState('error'); setMsg('无法预览此文件类型');
        }
      } catch (e) {
        if (!cancelled) { setMsg(e.message || '预览失败'); setState('error'); }
      }
    })();
    return () => { cancelled = true; if (madeUrl) URL.revokeObjectURL(madeUrl); };
  }, [att.id, kind]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="att-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="att-modal" onClick={e => e.stopPropagation()}>
        <div className="att-modal-head">
          <span className="att-modal-name" title={att.filename}>{att.filename}</span>
          <div className="att-actions">
            <button className="att-action" onClick={() => onDownload(att)} title="下载" aria-label="下载"><Icon name="download" /></button>
            <button className="att-action" onClick={onClose} title="关闭" aria-label="关闭"><Icon name="x" /></button>
          </div>
        </div>
        <div className="att-modal-body">
          {state === 'loading' && <div className="att-modal-msg"><span className="att-spinner" /> 加载中…</div>}
          {state === 'error' && <div className="att-modal-msg">{msg}</div>}
          {state === 'ready' && kind === 'image' && <img className="att-modal-img" src={url} alt={att.filename} />}
          {state === 'ready' && kind === 'pdf' && <iframe className="att-modal-pdf" src={url} title={att.filename} />}
          {state === 'ready' && kind === 'word' && <WordView blob={wordBlob} />}
          {state === 'ready' && kind === 'excel' && <ExcelView sheets={sheets} />}
        </div>
      </div>
    </div>
  );
}

export const AttachmentsPanel = ({ taskId, user, staff = [] }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total } during batch
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);    // attachment being previewed
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.listAttachments(taskId)
      .then(list => { if (!cancelled) { setItems(list); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e.message || '加载失败'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [taskId]);

  // Batch upload: validate + upload each file sequentially, prepend as it lands,
  // and keep going if one fails (collect a per-file failure summary at the end).
  async function doUploadMany(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setErr('');
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    let okCount = 0;
    const failed = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > MAX_BYTES) {
        failed.push(`${file.name}（过大 ${fmtSize(file.size)}）`);
      } else {
        try {
          const att = await api.uploadAttachment(taskId, file);
          setItems(prev => [att, ...prev]);
          okCount++;
        } catch (e) {
          failed.push(`${file.name}（${e.message || '失败'}）`);
        }
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setUploading(false);
    setProgress(null);
    if (failed.length) {
      setErr(`${okCount} 个成功，${failed.length} 个失败：${failed.join('；')}`);
    }
  }

  function onFilePick(e) {
    void doUploadMany(e.target.files);
    e.target.value = '';  // allow re-pick of same file(s)
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    void doUploadMany(e.dataTransfer.files);
  }

  async function onDelete(att) {
    if (!await confirmDialog({ title: '删除附件', kind: 'danger', confirmLabel: '删除', message: `删除 "${att.filename}"？` })) return;
    try {
      await api.deleteAttachment(att.id);
      setItems(prev => prev.filter(x => x.id !== att.id));
    } catch (e) {
      setErr(e.message || '删除失败');
    }
  }

  async function onDownload(att) {
    try {
      await api.downloadAttachment(att.id, att.filename);
    } catch (e) {
      setErr(e.message || '下载失败');
    }
  }

  function uploaderName(staffId) {
    return staff.find(s => s.id === staffId)?.name || staffId;
  }

  return (
    <section className="att-panel">
      <h3 className="att-title">附件 {items.length > 0 && <span className="att-count">({items.length})</span>}</h3>

      <div
        className={`att-dropzone ${dragOver ? 'over' : ''} ${uploading ? 'uploading' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="上传文件"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={onFilePick}
          disabled={uploading}
        />
        {uploading ? (
          <div className="att-uploading">
            <span className="att-spinner" /> 上传中{progress ? ` (${progress.done}/${progress.total})` : ''}…
          </div>
        ) : (
          <div className="att-dropzone-prompt">
            <Icon name="plus" /> 拖拽文件到这里，或点击选择（可多选）
            <div className="att-dropzone-sub">图片 / PDF / Word / Excel / CSV · 每个最大 25MB</div>
          </div>
        )}
      </div>

      {err && (
        <div className="att-error" role="alert">
          {err}
          <button className="att-error-dismiss" onClick={() => setErr('')} aria-label="关闭">×</button>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="att-empty">加载中…</div>
      ) : items.length === 0 ? (
        <div className="att-empty">还没有附件</div>
      ) : (
        <ul className="att-list">
          {items.map(att => {
            const canDelete = att.uploadedBy === user?.id || isElevated(user);
            const canPreview = !!previewKind(att);
            return (
              <li key={att.id} className="att-item">
                <span className="att-icon" aria-hidden="true">{fileIconKind(att.mime)}</span>
                <div className="att-meta">
                  <button
                    className="att-name"
                    onClick={() => canPreview ? setPreview(att) : onDownload(att)}
                    title={canPreview ? '点击预览' : '点击下载'}
                  >
                    {att.filename}
                  </button>
                  <div className="att-sub mono">
                    {fmtSize(att.sizeBytes)} · {uploaderName(att.uploadedBy)} · {fmtDateTime(att.uploadedAt)}
                  </div>
                </div>
                <div className="att-actions">
                  {canPreview && (
                    <button className="att-action" onClick={() => setPreview(att)} title="预览" aria-label="预览">
                      <Icon name="search" />
                    </button>
                  )}
                  <button className="att-action" onClick={() => onDownload(att)} title="下载" aria-label="下载">
                    <Icon name="download" />
                  </button>
                  {canDelete && (
                    <button className="att-action att-action-danger" onClick={() => onDelete(att)} title="删除" aria-label="删除">
                      <Icon name="x" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {preview && (
        <PreviewModal att={preview} kind={previewKind(preview)} onClose={() => setPreview(null)} onDownload={onDownload} />
      )}
    </section>
  );
};
